import { Injectable, inject } from '@angular/core';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { LabTestReportData} from '../models/lab-test';
import {  Fill, Font, Workbook } from 'exceljs';
import * as fs from 'file-saver';
import { PVT_CONSTANTS, reportConstants } from '../common/constants';
import { VwUserReportView } from '../models/project';
import { ProjectComments, SampleComments, SampleCommentsInfo } from '../models/project-comments';
import { ReportExcelBuildersService } from './reportpdfbuilders/report.excelBuilders.service';
import { ReportPDFBuildersService } from './reportpdfbuilders/report.pdfBuilders.service';

@Injectable({
  providedIn: 'root'
})
export class ReportService {
  private _reportExcelBuildersService = inject(ReportExcelBuildersService);
  private _reportPDFBuildersService = inject(ReportPDFBuildersService);

  // Generate PDF Report of sample
  GenerateReport(labTestData: LabTestReportData, pdfSelected: boolean, excelSelected: boolean, samplingType: string) {
    let reportType: string = reportConstants.reportTypeMDT; // Default report type
    if (samplingType.toLowerCase() == PVT_CONSTANTS.MDT.toLowerCase()) {
      reportType = reportConstants.reportTypeMDT;
    }
    else if (samplingType.toLowerCase() == PVT_CONSTANTS.DST.toLowerCase()) {
      reportType = reportConstants.reportTypeDST;
    }
    else if (samplingType.toLowerCase() == PVT_CONSTANTS.SurfaceSeparator.toLowerCase()) {
      reportType = reportConstants.reportTypeSeparatorTest;
    }

    if (pdfSelected === true) {
      this._reportPDFBuildersService.GeneratePdf(labTestData, reportType, false);
    }
    if (excelSelected === true) {
      this._reportExcelBuildersService.GenerateExcel(labTestData, reportType);
    }
  }

  ToDate(value: string | Date | null | undefined): Date | null | string {
    if (!value) return null;
    try {
      return typeof value === reportConstants.string ? new Date(value) : value;
    } catch {
      return null;
    }
  }

  async GenerateUserReportExcel(userData: VwUserReportView[]) {
    const wb = new Workbook();

    // ---------- Utility ----------
    const toDate = (value: string | Date | null | undefined): Date | null | string => {
      if (!value) return null;
      try {
        return typeof value === reportConstants.string ? new Date(value) : value;
      } catch {
        return null;
      }
    };

    // Common styles
    const headerFillBlue = { type: reportConstants.pattern, pattern: reportConstants.solid, fgColor: { argb: reportConstants.ff1f4e78 } }; // dark blue
    const headerFontWhite = { name: reportConstants.calibri, color: { argb: reportConstants.ffffffff }, bold: true, size: 12 };
    const borderThin = {
      top: { style: reportConstants.thin, color: { argb: reportConstants.ffBFBFBF } },
      left: { style: reportConstants.thin, color: { argb: reportConstants.ffBFBFBF } },
      bottom: { style: reportConstants.thin, color: { argb: reportConstants.ffBFBFBF } },
      right: { style: reportConstants.thin, color: { argb: reportConstants.ffBFBFBF } },
    };
    const dateFmtGroup = reportConstants.m_d_yyyy_h_mm;

    // ============================================================
    // Sheet 2: Grouped View (your second method)
    // ============================================================
    const grouped = wb.addWorksheet(reportConstants.userReport, {
      properties: { defaultRowHeight: 18 },
      views: [{ state: reportConstants.normal }]
    });

    // Styles for grouped
    const subHeaderFill = { type: reportConstants.pattern, pattern: reportConstants.solid, fgColor: { argb: reportConstants.ffBDD7EE } }; // light blue
    const tableHeaderFill = { type: reportConstants.pattern, pattern: reportConstants.solid, fgColor: { argb: reportConstants.ffDEEAF6 } }; // lighter
    const subHeaderFont = { name: reportConstants.calibri, color: { argb: reportConstants.ff000000 }, bold: true, size: 11 };
    const tableHeaderFont = { name: reportConstants.calibri, color: { argb: reportConstants.ff000000 }, bold: true, size: 11 };

    // Set base widths
    const widths = [16, 22, 14, 18, 20, 10, 18, 22, 22];
    widths.forEach((w, i) => (grouped.getColumn(i + 1).width = w));

    // Group by project
    const projects = new Map<string, VwUserReportView[]>();
    userData.forEach(r => {
      const key = r.projectNumber ?? reportConstants.unknownProject;
      if (!projects.has(key)) projects.set(key, []);
      projects.get(key)!.push(r);
    });
    const projectEntries = Array.from(projects.entries()).sort(([a], [b]) => a.localeCompare(b));

    let currentRow = 1;
    const addHeaderRow = (values: string[], fill: Fill, font: Partial<Font>) => {
      const row = grouped.getRow(currentRow++);
      row.values = values;
      row.height = 22;
      row.eachCell((cell) => {
        cell.fill = fill;
        cell.font = font;
        cell.alignment = { vertical: reportConstants.middle, horizontal: reportConstants.center };
        cell.border = borderThin;
      });
    };
    const addRow = (values: (string | number | Date | null)[]) => {
      const row = grouped.getRow(currentRow++);
      row.values = values;
      row.height = 18;
      row.eachCell((cell) => {
        cell.alignment = { vertical: reportConstants.middle, horizontal: reportConstants.left };
        cell.border = borderThin;
      });
      return row;
    };
    const addBlank = (count = 1) => { currentRow += count; };

    for (const [projectNumber, rows] of projectEntries) {
      const anyRow = rows[0];
      const projectName = anyRow.projectName ?? '';
      const country = anyRow.country ?? '';

      // Project header + data row
      addHeaderRow(reportConstants.projectColumns, headerFillBlue, headerFontWhite);
      addRow([projectNumber, projectName, country]);
      addBlank();

      // Group by well (wellName + sampleIdentifier)
      const wells = new Map<string, VwUserReportView[]>();
      rows.forEach(r => {
        const key = `${r.wellName ?? ''}|${r.sampleIdentifier ?? ''}`;
        if (!wells.has(key)) wells.set(key, []);
        wells.get(key)!.push(r);
      });

      for (const [wellKey, wrows] of wells.entries()) {
        const [wellName, sampleIdentifier] = wellKey.split('|');

        // Well header + data row
        addHeaderRow(reportConstants.wellColumns, subHeaderFill, subHeaderFont);
        addRow([wellName || '', sampleIdentifier || '']);

        // Table header
        addHeaderRow(
          reportConstants.experimentColumns,
          tableHeaderFill,
          tableHeaderFont
        );

        // Sort experiments by sequence (then by create time)
        const sorted = wrows.slice().sort((a, b) => {
          const seqA = a.experimentSequenceNo ?? 0;
          const seqB = b.experimentSequenceNo ?? 0;
          if (seqA !== seqB) return seqA - seqB;
          const tA = a.experimentCreateDatetime ? new Date(a.experimentCreateDatetime).getTime() : 0;
          const tB = b.experimentCreateDatetime ? new Date(b.experimentCreateDatetime).getTime() : 0;
          return tA - tB;
        });

        // Data rows
        sorted.forEach(r => {
          const createDate = toDate(r.experimentCreateDatetime);
          const updateDate = toDate(r.experimentUpdateTime);
          const row = addRow([
            r.experimentName ?? '',
            r.experimentSequenceNo ?? '',
            r.experimentCreatedBy ?? '',
            createDate ?? '',
            r.experimentUpdatedBy ?? '',
            updateDate ?? '',
            r.experimentStatus ?? ''
          ]);

          // Date columns in grouped table: indices 4 and 6
          const c4 = row.getCell(4);
          if (c4.value instanceof Date) c4.numFmt = dateFmtGroup;
          const c6 = row.getCell(6);
          if (c6.value instanceof Date) c6.numFmt = dateFmtGroup;
        });

        addBlank(); // space between wells
      }

      addBlank(1); // space between projects
    }

    // ---------- Save once ----------
    // Finally, generate & download
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: reportConstants.applicationSpreadsheet });

    const fileName = `${reportConstants.userReportExcel}_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}${reportConstants.xlsx}`;

    const formData = new FormData();
    formData.append(reportConstants.file, blob, fileName);

    fs.saveAs(blob, fileName + reportConstants.xlsx);
  }

  async GenerateProjectCommentsReport(projectNumber: string, projectComments: ProjectComments) {

    const doc = new jsPDF();
    doc.setFontSize(6);
    doc.setTextColor(255, 255, 255);
    doc.setFillColor(24, 46, 100);
    doc.rect(7, 7, 196, 6, reportConstants.fChar);
    doc.text(reportConstants.commentsHistoryReportForProject + projectNumber, 9, 11);
    doc.setTextColor(0, 0, 0);

    let tableStartY = 11;
    projectComments.sampleList.forEach(async sample => {
      SampleComments(sample);
    });

    const logo = new Image();
    const pvtImg = new Image();
    logo.src = '/' + reportConstants.exxonMobilLogo;
    pvtImg.src = '/' + reportConstants.pvtLabLogo;
    const totalPages = doc.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(10);
      doc.text(`${i}`, pageWidth - 7, pageHeight - 10, { align: reportConstants.right });
      doc.addImage(logo, reportConstants.pngCap, 7, 2, 20, 2);
      doc.text('  | ', 30, 4);
      doc.addImage(pvtImg, reportConstants.pngCap, 37, 2, 20, 2);

    }
    doc.save(projectNumber + '-' + reportConstants.commentsHistoryPdf);

    function SampleComments(sample: SampleCommentsInfo) {
      if (tableStartY > 255) {
        doc.addPage();
        tableStartY = 10;
      }
      autoTable(doc, {
        head: reportConstants.commentsHead,
        body: [[sample.sample, sample.well, sample.samplingType]],
        startY: tableStartY + 10,
        theme: reportConstants.grid,
        margin: { left: 7, right: 7 },
        styles: { fontSize: 4 },
        headStyles: { fillColor: [237, 237, 237], textColor: [0, 0, 0] },
        bodyStyles: { fillColor: [245, 245, 245] },
      });
      tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;

      if (sample.fluidTypeComments !== '') {
        const comments: SampleComments[] = [{ section: reportConstants.fluidTypeComments, comments: sample.fluidTypeComments, createdByUser: sample.createdByUser, createdTime: sample.createdTime }];
        SectionCommentsTable(comments);
      }
      if (sample.sampleTypeComments !== '') {
        const comments: SampleComments[] = [{ section: reportConstants.sampleTypeComments, comments: sample.sampleTypeComments, createdByUser: sample.createdByUser, createdTime: sample.createdTime }];
        SectionCommentsTable(comments);
      }

      if (sample.sampleComments.length > 0) {
        sample.sampleComments
          .reduce((acc: Map<string, SampleComments[]>, c: SampleComments) => {
            const section = (c.section ?? reportConstants.other).trim() || reportConstants.other;
            const bucket = acc.get(section) ?? acc.set(section, []).get(section)!;
            bucket.push({ ...c, section });
            return acc;
          }, new Map<string, SampleComments[]>())
          .forEach((comments: SampleComments[]) => {
            SectionCommentsTable(comments);
          })
      }

      if (sample.experimentComments.length > 0) {
        sample.experimentComments.forEach(async expComments => {
          const comments: SampleComments[] = expComments.comments.map(data => {
            return {
              section: `${reportConstants.experimentName}: ${expComments.experimentName} ${reportConstants.sequenceNo}: ${expComments.experimentSequenceNo}`,
              comments: data.notes,
              createdByUser: data.createdByUser,
              createdTime: data.createdTime
            } as SampleComments;
          });
          SectionCommentsTable(comments);
        });
      }
    }

    function SectionCommentsTable(comments: SampleComments[]) {
      if (tableStartY > 255) {
        doc.addPage();
        tableStartY = 10;
      }
      doc.text(comments[0].section, 7, tableStartY);
      autoTable(doc, {
        head: reportConstants.commentsHeadSecond,
        body: comments.map(c => [c.comments, c.createdByUser, c.createdTime]),
        startY: tableStartY + 2,
        theme: reportConstants.grid,
        margin: { left: 7, right: 7 },
        styles: { fontSize: 4 },
        headStyles: { fillColor: [237, 237, 237], textColor: [0, 0, 0] },
      });
      tableStartY = (doc.lastAutoTable?.finalY ?? doc.internal.pageSize.getHeight()) + 7;
    }
  }
}

